import { useState, useEffect } from 'react';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ModernHome } from './components/ModernHome';
import { PropertyCatalog } from './components/PropertyCatalog';
import { PropertyDetail } from './components/PropertyDetail';
import { AboutUs } from './components/AboutUs';
import { ModernServices } from './components/ModernServices';
import { ContactUs } from './components/ContactUs';
import { Blog } from './components/Blog';
import { AffordabilityCalculator } from './components/AffordabilityCalculator';
import { Calculators } from './components/Calculators';
import { AreaAlerts } from './components/AreaAlerts';
import { PreApproval } from './components/PreApproval';
import { AgentHub } from './components/AgentHub';
import { SellerHub } from './components/SellerHub';
import { AdminHub } from './components/AdminHub';
import { MarketingHub } from './components/MarketingHub';
import { ExternalAgentPortal } from './components/ExternalAgentPortal';
import { AgentDirectory } from './components/AgentDirectory';
import { AgentProfile } from './components/AgentProfile';
import { AgentReportPage } from './components/AgentReportPage';
import { DealDeskHub } from './components/DealDeskHub';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Login } from './components/Login';
import { AgentLogin } from './components/AgentLogin';
import { ComprehensiveDiagnostic } from './components/ComprehensiveDiagnostic';
import { ConfirmEmailPage } from './components/ConfirmEmailPage';
import { ForgotPasswordPage } from './components/ForgotPasswordPage';
import { OtpFormPage } from './components/OtpFormPage';
import { DocumentUploadPage } from './components/DocumentUploadPage';
import { ReferralLandingPage } from './components/ReferralLandingPage';
import { CompetitionRegisterPage } from './components/CompetitionRegisterPage';
import { MobileBottomNav } from './components/MobileBottomNav';
import { Property } from './types/property';
import { Agent } from './types/agent';
import { useTheme } from './hooks/useTheme';
import { useFavorites } from './hooks/useFavorites';
import { useRouteGuard, checkRouteAccess, type Page as RoutePage } from './hooks/useRouteGuard';
import { authService, User, UserRole } from './lib/auth';
import { restoreCognitoSession, cognitoLogout } from './lib/cognito-auth';
import { checkExternalAPIHealth, tokenManager, externalAuthAPI } from './lib/external-api';
import { loadRuntimeConfig } from './config/runtime-config';
import { updateAPIBase } from './lib/api-fetch';
import { logger } from './services/logger.service';
import { authStorage } from './lib/storage';
import apiClient from './services/api-client';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';
import { Button } from './components/ui/button';
import { LogIn as LogInIcon } from 'lucide-react';

// Initialize app
if (typeof window !== 'undefined') {
  // Display branding message
  console.log(
    '%cSomething Property Portal',
    'font-size: 20px; font-weight: bold; color: #2563eb;'
  );
  console.log(
    '%cBuilt by the team at Bondovation.\nWe have more in the pipeline, watch this space or just call us!',
    'font-size: 14px; color: #64748b;'
  );
  console.log('');

  // Load runtime configuration FIRST before any API calls
  loadRuntimeConfig()
    .then((config) => {
      // Initialize logger with config
      logger.initialize();
      logger.info('Runtime configuration loaded successfully', {
        environment: config.environment,
        apiBaseUrl: config.apiGateway.baseUrl,
      });

      // Update API client base URL
      apiClient.updateBaseURL(config.apiGateway.baseUrl);

      // Update apiFetch API_BASE so all fetch-based calls use the correct URL
      updateAPIBase();

      // Now safe to make API calls
      setTimeout(() => checkExternalAPIHealth(), 2000);
    })
    .catch(error => {
      console.error('Failed to load runtime configuration:', error);
      logger.error('Failed to load runtime configuration', error as Error);
    });
}

type Page =
  | 'home'
  | 'catalog'
  | 'detail'
  | 'about'
  | 'services'
  | 'contact'
  | 'calculator'
  | 'calculators'
  | 'area-alerts'
  | 'pre-approval'
  | 'agent-hub'
  | 'seller-hub'
  | 'admin-hub'
  | 'marketing-hub'
  | 'external-agent-portal'
  | 'agent-directory'
  | 'agent-profile'
  | 'agent-report'
  | 'hub'
  | 'seller'
  | 'admin'
  | 'marketing'
  | 'external-agent'
  | 'aws-diagnostic'
  | 'blog'
  | 'confirm-email'
  | 'forgot-password'
  | 'saved'
  | 'agent-login-page'
  | 'seller-login-page'
  | 'otp-form'
  | 'document-upload'
  | 'referral-landing'
  | 'competition-register'
  | 'dealdesk';

export default function App() {
  const [publicPageToken, setPublicPageToken] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const { theme } = useTheme();
  const { favorites, favoriteCount, toggleFavorite, isFavorite } = useFavorites();
  const [currentUser, setCurrentUser] = useState<User | null>(authService.getCurrentUser());
  const [showLogin, setShowLogin] = useState(false);
  const [showAgentLogin, setShowAgentLogin] = useState(false);
  const [previousPage, setPreviousPage] = useState<Page>('catalog');

  // Route guard - protect current page
  useRouteGuard(
    currentPage as RoutePage,
    (page) => setCurrentPage(page as Page),
    () => setShowAgentLogin(true),
    () => setShowLogin(true)
  );
  const [isImpersonating, setIsImpersonating] = useState(() => {
    try {
      const impersonation = authStorage.get<{ email: string; impersonatedBy: string; timestamp?: number }>('sp_impersonation');
      return impersonation || null;
    } catch { return null; }
  });

  useEffect(() => {
    // Subscribe to auth changes
    const unsubscribe = authService.subscribe((user) => {
      setCurrentUser(user);
    });

    // F-011/F-012: Handle direct URL navigation for role-specific login routes
    // Keep URL intact (do NOT replaceState) so browser URL matches what's rendered
    const pathname = window.location.pathname;
    if (pathname === '/agent/login' || pathname === '/agent-login') {
      setCurrentPage('agent-login-page');
    } else if (pathname === '/seller/login') {
      setCurrentPage('seller-login-page');
    } else if (pathname.startsWith('/otp/')) {
      setPublicPageToken(pathname.slice('/otp/'.length));
      setCurrentPage('otp-form');
    } else if (pathname.startsWith('/upload/')) {
      setPublicPageToken(pathname.slice('/upload/'.length));
      setCurrentPage('document-upload');
    } else if (pathname.startsWith('/join/')) {
      setPublicPageToken(pathname.slice('/join/'.length));
      setCurrentPage('referral-landing');
    } else if (pathname === '/competition-register') {
      setCurrentPage('competition-register');
    }

    // Handle admin impersonation via URL hash
    const hash = window.location.hash;

    // NEW SECURE METHOD: Use sessionStorage with key (tokens never in URL)
    if (hash.startsWith('#impersonate-key=')) {
      try {
        const impersonationKey = hash.slice('#impersonate-key='.length);
        const storageKey = `sp_imp_${impersonationKey}`;

        // Read tokens from sessionStorage
        const payloadStr = sessionStorage.getItem(storageKey);

        if (!payloadStr) {
          console.error('Impersonation key not found or expired');
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          return;
        }

        const payload = JSON.parse(payloadStr);

        // Immediately delete from sessionStorage (one-time use)
        sessionStorage.removeItem(storageKey);

        // Save tokens
        tokenManager.saveTokens({
          accessToken: payload.accessToken,
          idToken: payload.idToken,
          refreshToken: payload.refreshToken,
          expiresIn: payload.expiresIn || 3600,
        });
        authStorage.set('sp_cognito_session', 'true');
        authStorage.set('sp_impersonation', {
          email: payload.email,
          impersonatedBy: payload.impersonatedBy || 'admin',
          timestamp: payload.timestamp || Date.now(),
        });

        // Clear hash immediately
        window.history.replaceState(null, '', window.location.pathname + window.location.search);

        // Update impersonation state
        setIsImpersonating({ email: payload.email, impersonatedBy: payload.impersonatedBy || 'admin' });

        // Fetch user data and set session
        externalAuthAPI.getUser().then((userData: any) => {
          setCurrentUser(userData);
          setCurrentPage('external-agent-portal');
          toast.success(`Logged in as ${payload.email} (impersonated)`);
        }).catch((error) => {
          console.error('Failed to fetch impersonated user data:', error);
        });
      } catch (error) {
        console.error('Error processing impersonation key:', error);
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }
    // LEGACY METHOD: Direct tokens in URL (deprecated but maintained for backward compatibility)
    else if (hash.startsWith('#impersonate=')) {
      try {
        console.warn('⚠️ Using deprecated impersonation method with tokens in URL. Please upgrade admin panel.');
        const encoded = hash.slice('#impersonate='.length);
        const payload = JSON.parse(decodeURIComponent(encoded));

        // Save tokens
        tokenManager.saveTokens({
          accessToken: payload.accessToken,
          idToken: payload.idToken,
          refreshToken: payload.refreshToken,
          expiresIn: payload.expiresIn || 3600,
        });
        authStorage.set('sp_cognito_session', 'true');
        authStorage.set('sp_impersonation', {
          email: payload.email,
          impersonatedBy: payload.impersonatedBy || 'admin',
          timestamp: Date.now(),
        });

        // Clear hash immediately
        window.history.replaceState(null, '', window.location.pathname + window.location.search);

        // Update impersonation state
        setIsImpersonating({ email: payload.email, impersonatedBy: payload.impersonatedBy || 'admin' });

        // Fetch user data and set session
        externalAuthAPI.getUser().then((userData: any) => {
          if (userData) {
            const name = userData.name || userData.email || payload.email;
            const groups: string[] = userData.groups || [];
            let role: UserRole = 'agent';
            if (groups.includes('admin')) role = 'admin';
            else if (groups.includes('agent')) role = 'agent';
            else if (groups.includes('seller')) role = 'seller';
            else if (groups.includes('external-agent')) role = 'external-agent';
            else if (groups.includes('marketing')) role = 'marketing';

            const user: User = {
              id: userData.userId ? `cognito-${userData.userId}` : (userData.attributes?.sub ? `cognito-${userData.attributes.sub}` : payload.email),
              username: userData.email || payload.email,
              password: '',
              name,
              email: userData.email || payload.email,
              role,
            };
            authService.setCurrentUser(user);
            logger.info('Impersonation session started', { email: name, role });
            toast.success(`Impersonating ${name}`, {
              description: `You are now logged in as this user (${role}) in this tab`,
              duration: 6000,
            });
          }
        }).catch((err: any) => {
          logger.warn('Failed to fetch user data during impersonation', { error: err.message });
          toast.info(`Logged in as ${payload.email} (could not fetch profile)`);
        });
      } catch (err) {
        logger.error('Failed to parse impersonation payload', err as Error);
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }

    // Restore Cognito session on app load
    // F-056/F-058 fix: only call cognitoLogout() on actual auth failures (401).
    // Network errors (405, 500, offline) must NOT clear the local session —
    // the user object from authStorage is still valid and should survive network hiccups.
    restoreCognitoSession().then((user) => {
      if (user) {
        logger.info('Session restored', { userId: user.id, role: user.role });
      }
    }).catch((err) => {
      logger.error('Session restore failed', err);
      const status = (err as any)?.status ?? (err as any)?.response?.status ?? 0;
      if (status === 401 || status === 403) {
        // Genuine auth rejection — token is invalid, must log out
        cognitoLogout();
      } else {
        // Network error / 405 / 500 — keep existing local session intact
        logger.warn('Session restore network error — keeping local session', { status });
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    logger.info('User logout initiated', { userId: currentUser?.id });
    try {
      await cognitoLogout();
    } catch (err) {
      logger.error('Logout error (cleaning up session anyway)', err as Error);
    }
    // Always clean up state regardless of whether logout API call succeeded
    authStorage.remove('sp_impersonation');
    setIsImpersonating(null);
    setCurrentUser(null);
    setCurrentPage('home');
    setShowLogin(true);
    toast.success('Logged out successfully');
  };

  const handleLoginSuccess = () => {
    setShowLogin(false);
    setShowAgentLogin(false);
    const user = authService.getCurrentUser();

    logger.info('Login successful', { userId: user?.id, role: user?.role });

    // Navigate to appropriate hub based on role
    if (user?.role === 'agent') {
      setCurrentPage('hub');
    } else if (user?.role === 'seller') {
      setCurrentPage('seller');
    } else if (user?.role === 'external-agent') {
      setCurrentPage('external-agent');
    } else if (user?.role === 'admin') {
      setCurrentPage('admin');
    } else if (user?.role === 'marketing') {
      setCurrentPage('marketing');
    } else if (user?.role === 'deal-coordinator') {
      setCurrentPage('dealdesk');
    }
  };

  const handleNavigate = (page: string) => {
    // Use route guard to check access
    const canNavigate = checkRouteAccess(
      page as RoutePage,
      () => setShowAgentLogin(true),
      () => setShowLogin(true)
    );

    if (!canNavigate) {
      return;
    }

    logger.debug('Navigation', { from: currentPage, to: page });
    setCurrentPage(page as Page);
  };

  const handlePropertySelect = (property: Property) => {
    setPreviousPage(currentPage);
    setSelectedProperty(property);
    setCurrentPage('detail');
  };

  const handleAgentSelect = (agent: Agent) => {
    setSelectedAgent(agent);
    setCurrentPage('agent-profile');
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return (
          <ModernHome
            onViewCatalog={() => setCurrentPage('catalog')}
            onPropertySelect={handlePropertySelect}
          />
        );
      case 'catalog':
        return (
          <PropertyCatalog
            onPropertySelect={handlePropertySelect}
            favorites={favorites}
            onToggleFavorite={toggleFavorite}
          />
        );
      case 'detail':
        return selectedProperty ? (
          <PropertyDetail
            property={selectedProperty}
            onBack={() => setCurrentPage(previousPage)}
            isFavorite={isFavorite(selectedProperty.id)}
            onToggleFavorite={toggleFavorite}
          />
        ) : null;
      case 'about':
        return (
          <AboutUs
            onNavigateToProperties={() => setCurrentPage('catalog')}
            onNavigateToAgents={() => setCurrentPage('agent-directory')}
          />
        );
      case 'services':
        return (
          <ModernServices
            onNavigateToProperties={() => setCurrentPage('catalog')}
            onNavigateToHub={() => setCurrentPage('home')}
          />
        );
      case 'contact':
        return <ContactUs />;
      case 'blog':
        return <Blog />;
      case 'calculator':
        return <AffordabilityCalculator />;
      case 'calculators':
        return <Calculators />;
      case 'area-alerts':
        return <AreaAlerts onClose={() => setCurrentPage('home')} />;
      case 'pre-approval':
        return <PreApproval onNavigateToProperties={() => setCurrentPage('catalog')} />;
      case 'agent-hub':
        return <AgentHub onBackToCatalog={() => setCurrentPage('catalog')} />;
      case 'seller-hub':
        return <SellerHub onBackToCatalog={() => setCurrentPage('catalog')} onPropertySelect={handlePropertySelect} />;
      case 'admin-hub':
        return <AdminHub onBackToCatalog={() => setCurrentPage('catalog')} />;
      case 'marketing-hub':
        return <MarketingHub onBackToCatalog={() => setCurrentPage('catalog')} />;
      case 'external-agent-portal':
        return <ExternalAgentPortal onBackToCatalog={() => setCurrentPage('catalog')} />;
      case 'agent-directory':
        return <AgentDirectory onAgentSelect={handleAgentSelect} />;
      case 'agent-profile':
        return selectedAgent ? (
          <AgentProfile
            agent={selectedAgent}
            onBack={() => setCurrentPage('agent-directory')}
            onPropertySelect={handlePropertySelect}
          />
        ) : null;
      case 'agent-report':
        return <AgentReportPage onBack={() => setCurrentPage('admin')} />;
      case 'hub':
        return <AgentHub onBackToCatalog={() => setCurrentPage('catalog')} />;
      case 'seller':
        return <SellerHub onBackToCatalog={() => setCurrentPage('catalog')} onPropertySelect={handlePropertySelect} />;
      case 'admin':
        return <AdminHub onBackToCatalog={() => setCurrentPage('catalog')} />;
      case 'marketing':
        return <MarketingHub onBackToCatalog={() => setCurrentPage('catalog')} />;
      case 'external-agent':
        return <ExternalAgentPortal onBackToCatalog={() => setCurrentPage('catalog')} />;
      case 'aws-diagnostic':
        return <ComprehensiveDiagnostic />;
      case 'confirm-email':
        return (
          <ConfirmEmailPage
            onNavigate={handleNavigate}
            onLoginSuccess={handleLoginSuccess}
          />
        );
      case 'forgot-password':
        return (
          <ForgotPasswordPage
            onNavigate={handleNavigate}
          />
        );
      case 'saved':
        return (
          <PropertyCatalog
            onPropertySelect={handlePropertySelect}
            showSavedOnly
            favorites={favorites}
            onToggleFavorite={toggleFavorite}
          />
        );
      // F-011: /agent/login direct URL — render AgentLogin as full page (no catalog behind it)
      case 'agent-login-page':
        return (
          <AgentLogin
            onLoginSuccess={handleLoginSuccess}
            onBack={() => setCurrentPage('home')}
            onNavigate={handleNavigate}
          />
        );
      // F-012: /seller/login direct URL — render Login as full page (no catalog behind it)
      case 'seller-login-page':
        return (
          <Login
            onClose={() => setCurrentPage('home')}
            onLoginSuccess={handleLoginSuccess}
            onNavigate={handleNavigate}
          />
        );
      case 'otp-form':
        return <OtpFormPage token={publicPageToken} />;
      case 'document-upload':
        return <DocumentUploadPage token={publicPageToken} />;
      case 'referral-landing':
        return <ReferralLandingPage code={publicPageToken} />;
      case 'competition-register':
        return <CompetitionRegisterPage />;
      case 'dealdesk':
        return <DealDeskHub onBackToCatalog={() => setCurrentPage('catalog')} />;
      default:
        return (
          <ModernHome
            onViewCatalog={() => setCurrentPage('catalog')}
            onPropertySelect={handlePropertySelect}
          />
        );
    }
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="bg-background text-foreground min-h-screen flex flex-col">
        {isImpersonating && (
          <div className="bg-orange-600 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-3 relative z-50">
            <LogInIcon className="w-4 h-4" />
            <span>
              <strong>Impersonation Mode</strong> - You are viewing the app as <strong>{isImpersonating.email}</strong>
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-white hover:bg-white/20 border border-white/30"
              onClick={() => {
                authStorage.remove('sp_impersonation');
                cognitoLogout();
                setIsImpersonating(null);
                toast.info('Impersonation session ended - this tab has been logged out');
              }}
            >
              End Session
            </Button>
          </div>
        )}
        <ErrorBoundary componentName="Header">
          <Header
            currentPage={currentPage}
            onNavigate={handleNavigate}
            currentUser={currentUser}
            onLogout={handleLogout}
            onOpenLogin={() => setShowLogin(true)}
          />
        </ErrorBoundary>
        <ErrorBoundary componentName="MainContent">
          <main className="flex-1">
            {renderPage()}
          </main>
        </ErrorBoundary>
        <ErrorBoundary componentName="Footer">
          <Footer onNavigate={handleNavigate} />
        </ErrorBoundary>

        {/* Mobile Bottom Navigation */}
        <ErrorBoundary componentName="MobileBottomNav">
          <MobileBottomNav
            currentPage={currentPage}
            onNavigate={handleNavigate}
            favoriteCount={favoriteCount}
            isLoggedIn={!!currentUser}
            onOpenLogin={() => setShowLogin(true)}
          />
        </ErrorBoundary>

        {/* Login Modal */}
        {showLogin && (
          <ErrorBoundary componentName="LoginModal">
            <Login
              onLoginSuccess={handleLoginSuccess}
              onClose={() => setShowLogin(false)}
              onNavigate={handleNavigate}
            />
          </ErrorBoundary>
        )}

        {/* Agent Login Modal */}
        {showAgentLogin && (
          <ErrorBoundary componentName="AgentLoginModal">
            <AgentLogin
              onLoginSuccess={handleLoginSuccess}
              onBack={() => setShowAgentLogin(false)}
              onNavigate={handleNavigate}
            />
          </ErrorBoundary>
        )}
      </div>
      <Toaster richColors position="top-right" />
    </div>
  );
}
